import { korzetenBelul, tavolsagKm } from '@ertekbecslo/kozos/korzet';

export type KivetelMod = 'be' | 'ki';
export type Kivetelek = Readonly<Record<string, KivetelMod>>;

/** A `korzet_teruletek` RPC egy sora (és a mentett kivételek területe): a koordináta a lista számításához kell. */
export interface TeruletSor {
  kod: string;
  nev: string;
  tipus: string;
  lat: number | null;
  lon: number | null;
}

export interface Korzet { lat: number; lon: number; sugarKm: number }

export interface ListaElem {
  kod: string;
  nev: string;
  tipus: string;
  lat: number | null;
  lon: number | null;
  /** A tűtől számított távolság; koordináta nélküli területnél null (az SQL-ben az ilyen "benne van"). */
  tavolsagKm: number | null;
  termeszetes: 'belul' | 'kivul';
  kivetel: KivetelMod | null;
  /** Végül benne van-e a körzetben (a kivétel előbbre való a távolságnál). */
  vegso: boolean;
}

export const BUDAPEST = { lat: 47.4979, lon: 19.0402 } as const;
export const ALAP_SUGAR_KM = 20;
export const CSUSZKA_MIN_KM = 5;
export const CSUSZKA_MAX_KM = 80;

// Magyarország kerete: ugyanez az offices_korzet_ck megkötés az adatbázisban.
const LAT_HATAR = [45.7, 48.6] as const;
const LON_HATAR = [16.1, 22.9] as const;

/** A tű koordinátája a megengedett keretre vágva, 5 tizedesre kerekítve (numeric(8,5)). */
export function tuKorlat(lat: number, lon: number): { lat: number; lon: number } {
  const korlat = (x: number, [also, felso]: readonly [number, number]) => Math.round(Math.min(felso, Math.max(also, x)) * 1e5) / 1e5;
  return { lat: korlat(lat, LAT_HATAR), lon: korlat(lon, LON_HATAR) };
}

/** Csúszka-határok: 5–80 km, 1 km-es lépés; ha a tárolt sugár kívül esik, a határ igazodik hozzá. */
export function csuszkaHatar(sugarKm: number): { min: number; max: number; lepes: number } {
  return { min: Math.min(CSUSZKA_MIN_KM, sugarKm), max: Math.max(CSUSZKA_MAX_KM, sugarKm), lepes: 1 };
}

/**
 * Kezdő középpont: a meglévő körzet tűje; ha nincs, az `offices.varos` névvel pontosan egyező (és egyértelmű) terület
 * pontja; különben Budapest.
 */
export function kezdoKozeppont(
  korzet: { lat: number | null; lon: number | null },
  egyezoTeruletek: readonly { lat: number | null; lon: number | null }[],
): { lat: number; lon: number } {
  if (korzet.lat !== null && korzet.lon !== null) return { lat: korzet.lat, lon: korzet.lon };
  const [egyetlen, masik] = egyezoTeruletek;
  if (egyetlen && !masik && egyetlen.lat !== null && egyetlen.lon !== null) return { lat: egyetlen.lat, lon: egyetlen.lon };
  return { ...BUDAPEST };
}

/**
 * A lista a területekből és a körzetből, távolság szerint. A `sorok` az RPC eredménye; a `tovabbi` területek (pl. a
 * mentett kivételeké) csak akkor kerülnek be, ha van rájuk kivétel, így a +25 km-es kereten kívüli kivétel is látszik.
 */
export function korzetLista(
  sorok: readonly TeruletSor[],
  korzet: Korzet,
  kivetelek: Kivetelek,
  tovabbi: readonly TeruletSor[] = [],
): ListaElem[] {
  const lathato = new Map<string, TeruletSor>();
  for (const s of sorok) lathato.set(s.kod, s);
  for (const s of tovabbi) if (kivetelek[s.kod] && !lathato.has(s.kod)) lathato.set(s.kod, s);

  const lista = [...lathato.values()].map((s): ListaElem => {
    const tav = s.lat === null || s.lon === null ? null : tavolsagKm(korzet.lat, korzet.lon, s.lat, s.lon);
    // Egyetlen döntési képlet: ugyanaz, mint a lead-indításnál (kozos/korzet.ts); a kézi kivételt lejjebb külön kezeljük.
    const termeszetes = korzetenBelul(s, korzet, {}) ? 'belul' : 'kivul';
    const kivetel = kivetelek[s.kod] ?? null;
    return {
      kod: s.kod, nev: s.nev, tipus: s.tipus, lat: s.lat, lon: s.lon, tavolsagKm: tav, termeszetes, kivetel,
      vegso: kivetel ? kivetel === 'be' : termeszetes === 'belul',
    };
  });
  return lista.sort((a, b) => (a.tavolsagKm ?? Infinity) - (b.tavolsagKm ?? Infinity) || a.kod.localeCompare(b.kod));
}

/** Hány kerület és település van végül a körzetben. */
export function osszesito(lista: readonly ListaElem[]): { kerulet: number; telepules: number } {
  const bent = lista.filter((e) => e.vegso);
  return { kerulet: bent.filter((e) => e.tipus === 'kerulet').length, telepules: bent.filter((e) => e.tipus === 'telepules').length };
}

/** Kivétel-váltás egy elemre: természetesen belül lévőnél `ki` be/ki, kívül lévőnél `be` be/ki. */
export function kivetelValtas(kivetelek: Kivetelek, elem: Pick<ListaElem, 'kod' | 'termeszetes'>): Kivetelek {
  if (kivetelek[elem.kod]) {
    const { [elem.kod]: _, ...tobbi } = kivetelek;
    return tobbi;
  }
  return { ...kivetelek, [elem.kod]: elem.termeszetes === 'belul' ? 'ki' : 'be' };
}

export interface KorzetMentesTorzs {
  p_lat: number | null;
  p_lon: number | null;
  p_sugar_km: number | null;
  p_kivetelek: { terulet_kod: string; mod: KivetelMod }[];
}

/**
 * A `korzet_mentes` paraméterei (az iroda azonosítója nélkül). A tű- vagy sugárváltás után fölöslegessé vált kivételek
 * (belül lévőn `be`, kívül lévőn `ki`) kimaradnak. `korzet = null`: nincs körzet, mind null és üres kivétellista.
 */
export function korzetMentesTorzs(korzet: Korzet | null, lista: readonly ListaElem[]): KorzetMentesTorzs {
  if (!korzet) return { p_lat: null, p_lon: null, p_sugar_km: null, p_kivetelek: [] };
  const kivetelek = lista
    .filter((e): e is ListaElem & { kivetel: KivetelMod } => e.kivetel !== null && e.kivetel !== (e.termeszetes === 'belul' ? 'be' : 'ki'))
    .map((e) => ({ terulet_kod: e.kod, mod: e.kivetel }));
  return { p_lat: korzet.lat, p_lon: korzet.lon, p_sugar_km: korzet.sugarKm, p_kivetelek: kivetelek };
}

/** A PostgREST `max_rows` (supabase/config.toml) és egyben a lapméret: ennél hosszabb eredményt a szerver némán levág. */
export const LAPMERET = 1000;

/**
 * Lapozott lekérés: a `lap(tol, ig)` az `.range(tol, ig)` (zárt tartomány) sorait adja; teljes lapnál jön a következő,
 * a rövidebb (vagy üres) lap a vége.
 */
export async function osszesLap<T>(lap: (tol: number, ig: number) => Promise<T[]>, lapmeret: number = LAPMERET): Promise<T[]> {
  const mind: T[] = [];
  for (let tol = 0; ; tol += lapmeret) {
    const sorok = await lap(tol, tol + lapmeret - 1);
    mind.push(...sorok);
    if (sorok.length < lapmeret) return mind;
  }
}
